
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import ProductTour from 'src/components/docs/ProductTour/ProductTour';
export const metadata = {
  toc: true,
  layout: 'guide',
  description: 'Multi-Item Link allows users to connect accounts from multiple institutions in a single Link session'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Callout = makeShortcode("Callout");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <Header title='Multi-Item Link' subtitle='Allow end users to connect accounts from multiple institutions in a single Link session' mdxType="Header" />
    <h4 {...{
      "id": "overview"
    }}>{`Overview`}</h4>
    <p>{`Multi-Item Link allows end users to add multiple Items in the same Link session. This flow is designed for contexts where you expect the user to link multiple accounts; for example, to provide a complete financial picture for a lending or personal finance use case. `}</p>
    <p>{`Multi-Item Link can result in more Items being connected per user, due to reduced friction, and can simplify your app's logic and user experience. `}</p>
    <p>{`Multi-Item Link is compatible with the following Plaid products: Auth, Transfer, Transactions, Liabilities, Investments, Bank Income, Assets, and Plaid Check Consumer Report (including Income and Partner Insights). It does not require special enablement and is automatically available to all customers.`}</p>
    <p>{`Multi-Item Link is not compatible with `}<a parentName="p" {...{
        "href": "/docs/link/embedded-institution-search"
      }}>{`Embedded Institution Search`}</a>{`. Multi-Item Link is also not currently compatible with `}<a parentName="p" {...{
        "href": "/docs/auth/coverage/same-day/"
      }}>{`non-credential-based Auth flows`}</a>{` such as Database Auth, Same-Day Micro-deposits, or Instant Micro-deposits.`}</p>
    <ProductTour steps={[{
      text: 'An example multi-Item Link flow. Your exact flow may differ.',
      image: '/assets/img/docs/link-tour/link-1-rm.png'
    }, {
      text: '...the user selects their bank...',
      image: '/assets/img/docs/link-tour/link-2-original-backup.png'
    }, {
      text: '...is directed to its OAuth flow...',
      image: '/assets/img/docs/link-tour/link-3-original-backup.png'
    }, {
      text: '...signs in via OAuth...',
      image: '/assets/img/docs/link-tour/link-4.png'
    }, {
      text: '...and selects which accounts to share.',
      image: '/assets/img/docs/link-tour/link-select-accounts.png'
    }, {
      text: 'Adding another bank will return the user to the "Select your institution" pane. Clicking "Continue" will complete the flow.',
      image: '/assets/img/docs/link-tour/multi-item-link.png'
    }, {
      text: 'In the guest flow, they can optionally save their most recently added account for fast access next time.',
      image: '/assets/img/docs/link-tour/link-6.png'
    }, {
      text: 'Once the phone number is verified, Link will close automatically.',
      image: '/assets/img/docs/link-tour/link-7.png'
    }]} defaultGalleryView={true} mdxType="ProductTour" />
    <h4 {...{
      "id": "integration-process"
    }}>{`Integration process`}</h4>
    <h5 {...{
      "id": "creating-a-link-token"
    }}>{`Creating a Link token`}</h5>
    <p>{`First, call `}<inlineCode parentName="p">{`/user/create`}</inlineCode>{` to obtain a `}<inlineCode parentName="p">{`user_id`}</inlineCode>{` (and, for legacy integrations begun before December 10, 2025, a `}<inlineCode parentName="p">{`user_token`}</inlineCode>{`).`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash",
        "metastring": "switcher=false  #Sample /user/create request for Multi-Item Link",
        "switcher": "false",
        "": true,
        "#Sample": true,
        "/user/create": true,
        "request": true,
        "for": true,
        "Multi-Item": true,
        "Link": true
      }}>{`// client_user_id is your own internal identifier for the end user
{
    "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
    "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
    "client_user_id" : "c0e2c4ee-b763-4af5-cfe9-46a46bce883d" 
}
`}</code></pre>
    <p>{`Next, call `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` with the user identifier you just created, and the `}<inlineCode parentName="p">{`enable_multi_item_link`}</inlineCode>{` field set to `}<inlineCode parentName="p">{`true`}</inlineCode>{`. If you received a `}<inlineCode parentName="p">{`user_token`}</inlineCode>{` from `}<inlineCode parentName="p">{`/user/create`}</inlineCode>{`, you should use that as your identifier. If you are using the newer integration flow that returns only a `}<inlineCode parentName="p">{`user_id`}</inlineCode>{` and does not return a `}<inlineCode parentName="p">{`user_token`}</inlineCode>{`, you should use the `}<inlineCode parentName="p">{`user_id`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash",
        "metastring": "switcher=false  #Sample /link/token/create request for Multi-Item Link",
        "switcher": "false",
        "": true,
        "#Sample": true,
        "/link/token/create": true,
        "request": true,
        "for": true,
        "Multi-Item": true,
        "Link": true
      }}>{`// Sample link/token/create request
{
    "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
    "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
    "client_name" : "Plaid Test App",
    "user_id":  "usr_9nSp2KuZ2x4JDw",
    "enable_multi_item_link": true,
    "products": ["transactions"],
    "country_codes": ["US"],
    "language": "en"
}
`}</code></pre>
    <p>{`Once you have created a Link token, you will launch `}<a parentName="p" {...{
        "href": "/docs/link"
      }}>{`Link`}</a>{` as normal, according to the standard instructions for your platform.`}</p>
    <h5 {...{
      "id": "obtaining-a-public-token"
    }}>{`Obtaining a public token`}</h5>
    <p>{`In most other Plaid integration methods, upon completion of the Link flow, you will receive a public token via the `}<inlineCode parentName="p">{`onSuccess`}</inlineCode>{` callback. In a Multi-Item Link session, `}<inlineCode parentName="p">{`onSuccess`}</inlineCode>{` is still called, but its `}<inlineCode parentName="p">{`public_token`}</inlineCode>{` argument will be empty; you will instead receive information about the Link flow (including the `}<inlineCode parentName="p">{`public_token`}</inlineCode>{` array) via the `}<a parentName="p" {...{
        "href": "/docs/api/link/#session_finished"
      }}><inlineCode parentName="a">{`SESSION_FINISHED`}</inlineCode></a>{` webhook.`}</p>
    <p>{`When the entire Multi-Item Link flow is complete and the user has exited Link, Plaid will fire a `}<a parentName="p" {...{
        "href": "/docs/api/link/#session_finished"
      }}><inlineCode parentName="a">{`SESSION_FINISHED`}</inlineCode></a>{` webhook that contains information about what caused the session to end, as well as the public tokens if the session completed successfully. `}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "switcher=false #Sample SESSION_FINISHED webhook",
        "switcher": "false",
        "#Sample": true,
        "SESSION_FINISHED": true,
        "webhook": true
      }}>{`{
  "webhook_type": "LINK",
  "webhook_code": "SESSION_FINISHED",
  "status": "success",
  "link_session_id": "356dbb28-7f98-44d1-8e6d-0cec580f3171",
  "link_token": "link-sandbox-af1a0311-da53-4636-b754-dd15cc058176",
  "public_tokens": [
    "public-sandbox-b0e2c4ee-a763-4df5-bfe9-46a46bce993d"
  ],
  "environment": "sandbox"
}
`}</code></pre>
    <p>{`If you want to start getting results as soon as each Item is added, you can listen for the `}<a parentName="p" {...{
        "href": "/docs/api/link/#item_add_result"
      }}><inlineCode parentName="a">{`ITEM_ADD_RESULT`}</inlineCode></a>{` webhook, which will fire after each completed Item add within the Link session. `}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "switcher=false #Sample ITEM_ADD_RESULT webhook",
        "switcher": "false",
        "#Sample": true,
        "ITEM_ADD_RESULT": true,
        "webhook": true
      }}>{`{
  "webhook_type": "LINK",
  "webhook_code": "ITEM_ADD_RESULT",
  "link_session_id": "356dbb28-7f98-44d1-8e6d-0cec580f3171",
  "link_token": "link-sandbox-af1a0311-da53-4636-b754-dd15cc058176",
  "public_token": "public-sandbox-b0e2c4ee-a763-4df5-bfe9-46a46bce993d",
  "environment": "sandbox"
}
`}</code></pre>
    <p>{`Detailed session data, including the public tokens and account metadata, will also be available from `}<inlineCode parentName="p">{`/link/token/get`}</inlineCode>{` for six hours after the session has completed.  In general, it is recommended to use the webhook to obtain the public token, since it will allow you to get the public token more promptly, but you may want to use `}<inlineCode parentName="p">{`/link/token/get`}</inlineCode>{` if your integration does not use webhooks, or as a backup mechanism if your system missed webhooks due to an outage. `}</p>
    <Callout warning mdxType="Callout">
      <p>{`When using Multi-Item Link, make sure to obtain the `}<inlineCode parentName="p">{`public_token`}</inlineCode>{` from either the `}<inlineCode parentName="p">{`SESSION_FINISHED`}</inlineCode>{` webhook, the `}<inlineCode parentName="p">{`ITEM_ADD_RESULT`}</inlineCode>{` webhook, or the `}<inlineCode parentName="p">{`results.item_add_results`}</inlineCode>{` object returned by `}<inlineCode parentName="p">{`/link/token/get`}</inlineCode>{`. The `}<inlineCode parentName="p">{`on_success`}</inlineCode>{` object returned by `}<inlineCode parentName="p">{`/link/token/get`}</inlineCode>{` is deprecated and should not be used to obtain the public token in Multi-Item Link flows, as it will contain only one public token, rather than all of the public tokens from the session.`}</p>
    </Callout>
    <h5 {...{
      "id": "frontend-changes"
    }}>{`Frontend changes`}</h5>
    <p>{`When using Multi-Item Link, `}<inlineCode parentName="p">{`onSuccess`}</inlineCode>{` is still called when the session completes, but its `}<inlineCode parentName="p">{`public_token`}</inlineCode>{` and metadata (such as institution ID or account mask) will be empty, since a single Link session can now add more than one Item. `}<inlineCode parentName="p">{`onExit`}</inlineCode>{` and `}<inlineCode parentName="p">{`onEvent`}</inlineCode>{` continue to fire as usual; to detect completion on the frontend, use the `}<inlineCode parentName="p">{`HANDOFF`}</inlineCode>{` event in `}<inlineCode parentName="p">{`onEvent`}</inlineCode>{` rather than relying on `}<inlineCode parentName="p">{`onSuccess`}</inlineCode>{`. To obtain the public tokens and metadata for each linked Item, use the `}<inlineCode parentName="p">{`results.item_add_results`}</inlineCode>{` object returned by `}<inlineCode parentName="p">{`/link/token/get`}</inlineCode>{`, or the `}<inlineCode parentName="p">{`SESSION_FINISHED`}</inlineCode>{` webhook.`}</p>
    <h4 {...{
      "id": "multi-item-link-with-multiple-products"
    }}>{`Multi-Item Link with multiple products`}</h4>
    <p>{`If you are using Multi-Item Link, the same Link token settings will be used for every Item in the Link flow. This means, for example, that Auth with Same-Day Micro-deposits cannot be used in the same Multi-Item Link flow as Income, because Auth must be initialized by itself to be used with Same-Day Micro-deposits. `}</p>
    <h5 {...{
      "id": "using-auth-or-transfer-with-multi-item-link"
    }}>{`Using Auth or Transfer with Multi-Item Link`}</h5>
    <p>{`A common use case for including Auth or Transfer as part of a Multi-Item Link process is when you want one of the linked accounts to be used for sending or receiving payments. For example, as a lender, you may want to disburse the user's loan to a linked account, or as a financial management app, you may want to use one of the linked accounts to collect a monthly subscription fee. `}</p>
    <p>{`In this situation, your app will want to connect to multiple institutions, but only need one account for payment purposes. For this use case, the recommended flow is:`}</p>
    <ol>
      <li parentName="ol">
        <p parentName="li">{`Put `}<inlineCode parentName="p">{`auth`}</inlineCode>{` in the `}<inlineCode parentName="p">{`optional_products`}</inlineCode>{` array when calling `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{`; this will request Auth permissions when possible, but will not block linking accounts that don't support Auth and won't cause you to be billed for Auth unless you use an Auth endpoint on the Item.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Locate eligible accounts by looking at the `}<inlineCode parentName="p">{`/link/token/get`}</inlineCode>{` response object and traversing `}<inlineCode parentName="p">{`link_sessions[].results.item_add_results[].accounts[].subtype`}</inlineCode>{` to check each account on each Item, to see if it is of subtype `}<inlineCode parentName="p">{`checking`}</inlineCode>{` or `}<inlineCode parentName="p">{`savings`}</inlineCode>{`. `}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`For each Item on an account where the subtype is `}<inlineCode parentName="p">{`checking`}</inlineCode>{` or `}<inlineCode parentName="p">{`savings`}</inlineCode>{`, call `}<inlineCode parentName="p">{`/item/get`}</inlineCode>{` and check the `}<inlineCode parentName="p">{`item.products[]`}</inlineCode>{` array for `}<inlineCode parentName="p">{`auth`}</inlineCode>{`. `}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`If the account subtype is `}<inlineCode parentName="p">{`checking`}</inlineCode>{` or `}<inlineCode parentName="p">{`savings`}</inlineCode>{`, and the `}<inlineCode parentName="p">{`item.products[]`}</inlineCode>{` value contains `}<inlineCode parentName="p">{`"auth"`}</inlineCode>{` as a value, then the account is eligible for use with Plaid Auth or Transfer. Present a UI in which the user can select an eligible account to use for sending or receiving funds. To avoid confusion, it is recommended that you present this UI even if there is only one eligible account. `}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`(Optional) You can add a button to this UI to link a different account to use for funds transfer, and have that button launch Link with just `}<inlineCode parentName="p">{`auth`}</inlineCode>{` or `}<inlineCode parentName="p">{`transfer`}</inlineCode>{`, which could allow the end user to link a payment account via a non-credential-based flow, such as Same-Day Micro-deposits or Database Auth. `}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Once the user has selected an account to use for payments, call the appropriate endpoint, such as `}<inlineCode parentName="p">{`/auth/get`}</inlineCode>{`, `}<inlineCode parentName="p">{`/processor/token/create`}</inlineCode>{`, or `}<inlineCode parentName="p">{`/transfer/authorization/create`}</inlineCode>{`, on the corresponding Item.`}</p>
      </li>
    </ol>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "overview",
        level: 4,
        title: "Overview",
      },
{
        id: "integration-process",
        level: 4,
        title: "Integration process",
      },
{
        id: "multi-item-link-with-multiple-products",
        level: 4,
        title: "Multi-Item Link with multiple products",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
